<!--
 * @Author: Nemo
 * @Description: Token支付组件
 * @Date: 2025-01-13
-->
<template>
  <div class="token-payment-container">
    <!-- 卡片列表 -->
    <div class="saved-cards-list">
      <!-- 卡片列表内容 - 使用transition实现折叠动画，由父组件控制展开/收起 -->
      <transition name="collapse">
        <div v-show="expanded" class="cards-list-content" :style="{ '--hover-brand-color': hoverBrandColor }">
          <div v-for="card in tokenCards" :key="card.id" :class="['card-item', {
            active: currentCardId === card.id,
            expired: card.isExpired
          }]" @click="selectCard(card)">

            <div class="card-item-header">
              <!-- 卡片品牌logo -->
              <span class="card-brand">
                <img :src="card.logoUrl" :alt="card.paymentMethod" />
              </span>
              <!-- 卡号 -->
              <span class="card-number">{{ formatCardNumber(card.cardNumber) }}</span>
              <!-- 过期状态 - 默认靠右，选中时被解绑按钮挤到中间 -->
              <span v-if="card.isExpired" class="expired-tag">{{ $t('message.checkout.expired') }}</span>
              <!-- 移除按钮 - 选中时显示 -->
              <div v-if="currentCardId === card.id" class="remove-btn" @click.stop="handleRemoveCard(card.id)">
                <span class="delete-icon-wrapper">
                  <img src="@/assets/images/delete.png" alt="delete" class="delete-icon" />
                </span>
                <span class="remove-text">{{ $t('message.checkout.removeCard') }}</span>
              </div>
            </div>

            <!-- 验证字段区域 - 选中且需要展开时显示 -->
            <div v-if="currentCardId === card.id && shouldExpand" class="card-verify-section">
              <!-- 支付字段 -->
              <dynamic-form v-if="getPaymentVerifyConfig.length > 0" ref="dynamicFormRefPayment"
                :config="getPaymentVerifyConfig" :validation-mode="'format-only'" :show-submit-button="false"
                @field-change="handleFieldChange" />

              <!-- 账单字段 -->
              <dynamic-form v-if="getBillingVerifyConfig.length > 0" ref="dynamicFormRefBilling"
                :config="getBillingVerifyConfig" :validation-mode="'format-only'" :show-submit-button="false"
                @field-change="handleFieldChange" />
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Pay按钮 -->
    <div class="token-pay-btn-wrapper">
      <on-button :loading="submitBtnLoading" :disabled="isSelectedCardExpired" type="button" @click="handleSubmit">
        {{ $t('message.checkout.pay') }} {{ order && formatAmount(order.txnAmount) }} {{ order && order.txnCurrency }}
      </on-button>
    </div>

    <!-- 切换到新卡支付 -->
    <div class="switch-to-card-wrapper">
      <div class="use-new-card-btn" @click="handleSwitchToCard">
        {{ $t('message.checkout.useNewCard') }}
      </div>
    </div>

    <!-- 移除卡片确认弹窗 -->
    <on-dialog ref="removeDialog" @confirm="confirmRemoveCard" @cancel="cancelRemoveCard">
      <div>{{ $t('message.checkout.removeCardDescription') }}</div>
    </on-dialog>
  </div>
</template>

<script>
import OnButton from '@/componentsV2/base/button/index.vue';
import OnDialog from '@/componentsV2/base/dialog/index.vue';
import { DynamicForm } from '@/componentsV2/busi/dynamic-form';
import { getFieldConfig } from '@/componentsV2/busi/dynamic-form/filed-manager';

export default {
  name: 'TokenPayment',
  components: {
    OnButton,
    OnDialog,
    DynamicForm
  },
  props: {
    // 订单信息
    order: {
      type: Object,
      required: true
    },
    // Token卡片列表
    tokenCards: {
      type: Array,
      default: () => []
    },
    // 提交按钮loading状态
    submitBtnLoading: {
      type: Boolean,
      default: false
    },
    // 选中的卡片ID（支持.sync）
    selectedCardId: {
      type: String,
      default: ''
    },
    // 是否展开卡片列表（支持.sync）- 开发者：Nemo
    expanded: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      formData: {},  // 动态表单数据
      currentCardId: this.selectedCardId,  // 当前选中的卡片ID
      requiredFields: {},  // 当前选中卡片需要的字段
      pendingRemoveCardId: null,  // 待移除的卡片ID
      brandColor: ''  // 品牌色
    };
  },
  mounted() {
    // 默认选中第一张未过期的卡片 - 开发者：Nemo
    this.selectDefaultCard();
    // 获取品牌色 - 开发者：Nemo
    this.getBrandColor();
  },
  computed: {
    /**
     * 计算 hover 时的品牌色（加深8%）
     * @author Nemo
     */
    hoverBrandColor() {
      return this.getHoverColor(this.brandColor);
    },

    /**
     * 当前选中的卡片
     * @author Nemo
     */
    selectedCard() {
      return this.tokenCards.find(c => c.id === this.currentCardId);
    },

    /**
     * 判断当前卡片是否需要展开
     * @author Nemo
     */
    shouldExpand() {
      if (!this.selectedCard) {
        return false;
      }
      // 过期的卡不展开
      if (this.selectedCard.isExpired) {
        return false;
      }
      // 有验证字段配置才展开
      return this.getPaymentVerifyConfig.length > 0 || this.getBillingVerifyConfig.length > 0;
    },

    /**
     * 判断当前选中的卡片是否过期
     * @author Nemo
     */
    isSelectedCardExpired() {
      return this.selectedCard && this.selectedCard.isExpired;
    },

    /**
     * 获取选中卡片的支付字段配置
     * @author Nemo
     */
    getPaymentVerifyConfig() {
      if (!this.selectedCard) {
        return [];
      }

      // 只处理 paymentConfigurationFields
      const paymentFields = this.selectedCard.paymentConfigurationFields || [];

      // 过滤掉分期相关的字段，Token支付不支持分期
      const filteredFields = paymentFields.filter(
        field => !['periodValue', 'installment', 'instalment', 'period'].includes(field.name)
      );

      const config = getFieldConfig({
        apiFields: filteredFields,
        fieldType: 'payment',
        country: this.order?.paymentCountry,
      });

      // 为 cvv 字段添加自定义输入处理和suffix
      const cvvField = config.find(field => field.name === 'cvv');
      if (cvvField) {
        cvvField.inputmode = 'numeric'; // 移动端显示数字键盘
        cvvField.inputHandler = 'cvvInputHandler'; // 只允许数字输入
        // 添加CVV图标 - 使用灵活的配置方式
        cvvField.suffix = {
          type: 'image',
          src: require('@/assets/images/icon-cvv.png'),
          alt: 'CVV',
          style: {
            width: '32px',
            height: '25px',
            transform: 'translateY(-3px)'
          }
        };
      }

      return config;
    },

    /**
     * 获取选中卡片的账单字段配置
     * @author Nemo
     */
    getBillingVerifyConfig() {
      if (!this.selectedCard) {
        return [];
      }

      // 只处理 billConfigurationFields
      const billingFields = this.selectedCard.billConfigurationFields || [];

      return getFieldConfig({
        apiFields: billingFields,
        fieldType: 'billing',
        country: this.order?.paymentCountry,
      });
    }
  },
  methods: {
    /**
     * 格式化金额
     * @author Nemo
     */
    formatAmount(amount) {
      if (!amount) return '0.00';
      const num = parseFloat(amount);
      return num.toFixed(2);
    },

    /**
     * CVV 输入处理 - 只允许数字
     * @author Nemo
     */
    cvvInputHandler(event) {
      const value = event.target.value.replace(/[^\d]/g, '');
      event.target.value = value;
    },

    /**
     * 格式化卡号，只显示最后4位，前面用点代替
     * @author Nemo
     */
    formatCardNumber(cardNumber) {
      if (!cardNumber) return '';
      const lastFour = cardNumber.slice(-4);
      return `···· ${lastFour}`;
    },

    /**
     * 默认选中第一张未过期的卡片
     * @author Nemo
     */
    selectDefaultCard() {
      // 如果已经有选中的卡片，不需要默认选中
      if (this.currentCardId) return;

      // 如果没有卡片，不需要选中
      if (!this.tokenCards || this.tokenCards.length === 0) return;

      // 查找第一张未过期的卡片
      const firstValidCard = this.tokenCards.find(card => !card.isExpired);

      // 如果找到了，选中它
      if (firstValidCard) {
        this.selectCard(firstValidCard);
      }
    },

    /**
     * 选择卡片
     * @author Nemo
     */
    selectCard(card) {
      this.currentCardId = card.id;
      this.formData = {};
      this.requiredFields = this.parseRequiredFields(card);
      this.$emit('update:selectedCardId', card.id);
    },

    /**
     * 处理字段变化
     * @author Nemo
     */
    handleFieldChange({ field, value }) {
      this.formData[field] = value;
    },

    /**
     * 解析必填字段
     * @author Nemo
     */
    parseRequiredFields(card) {
      const fields = {};
      if (card.paymentConfigurationFields) {
        card.paymentConfigurationFields.forEach(field => {
          if (field.require === 'Y' && field.display === 'Y') {
            fields[field.name] = '';
          }
        });
      }
      return fields;
    },

    /**
     * 验证表单
     * @author Nemo
     */
    async validateForm() {
      if (!this.currentCardId) {
        this.$message.warning('Please select a card');
        return false;
      }

      const selectedCard = this.tokenCards.find(c => c.id === this.currentCardId);
      if (!selectedCard) return false;

      // 参考 PaymentForm.vue 的验证逻辑
      const paymentForm = this.$refs.dynamicFormRefPayment;
      const billingForm = this.$refs.dynamicFormRefBilling;

      // 设置为完整验证模式
      if (paymentForm && paymentForm[0]) {
        paymentForm[0].setValidationMode('full');
      }
      if (billingForm && billingForm[0]) {
        billingForm[0].setValidationMode('full');
      }

      // 创建验证任务数组
      const validationTasks = [];

      // 处理 ref 数组情况
      const paymentFormInstance = Array.isArray(paymentForm) ? paymentForm[0] : paymentForm;
      const billingFormInstance = Array.isArray(billingForm) ? billingForm[0] : billingForm;

      if (paymentFormInstance) {
        validationTasks.push(
          paymentFormInstance.handleSubmit().then(result => ({
            form: 'payment',
            ref: paymentFormInstance,
            result
          }))
        );
      }

      if (billingFormInstance) {
        validationTasks.push(
          billingFormInstance.handleSubmit().then(result => ({
            form: 'billing',
            ref: billingFormInstance,
            result
          }))
        );
      }

      if (validationTasks.length === 0) {
        return true;
      }

      // 并行验证所有表单
      const results = await Promise.all(validationTasks);

      // 检查所有表单是否都通过验证
      const allValid = results.every(item => item.result.valid);

      if (!allValid) {
        // 查找第一个验证失败的表单
        const failedResult = results.find(item => !item.result.valid);
        if (failedResult) {
          await failedResult.ref.scrollToFirstError();
        }
        return false;
      }

      return true;
    },

    /**
     * 提交Token支付
     * @author Nemo
     */
    async handleSubmit() {
      const isValid = await this.validateForm();
      if (!isValid) return;

      const selectedCard = this.tokenCards.find(c => c.id === this.currentCardId);

      // 收集所有表单数据
      const formData = {};

      const paymentForm = this.$refs.dynamicFormRefPayment;
      const billingForm = this.$refs.dynamicFormRefBilling;

      const paymentFormInstance = Array.isArray(paymentForm) ? paymentForm[0] : paymentForm;
      const billingFormInstance = Array.isArray(billingForm) ? billingForm[0] : billingForm;

      if (paymentFormInstance) {
        Object.assign(formData, paymentFormInstance.getFormData());
      }
      if (billingFormInstance) {
        Object.assign(formData, billingFormInstance.getFormData());
      }

      this.$emit('submit', {
        card: selectedCard,
        formData: formData
      });
    },

    /**
     * 移除卡片
     * @author Nemo
     */
    handleRemoveCard(cardId) {
      this.pendingRemoveCardId = cardId;
      this.$refs.removeDialog.open({
        title: this.$t('message.checkout.removeCardTitle'),
        confirmText: this.$t('message.checkout.remove'),
        cancelText: this.$t('message.checkout.cancel'),
        closeOnClickMask: true,
        showDefaultFooter: true
      });
    },

    /**
     * 确认移除卡片
     * @author Nemo
     */
    confirmRemoveCard() {
      if (this.pendingRemoveCardId) {
        this.$emit('remove-card', this.pendingRemoveCardId);
        this.pendingRemoveCardId = null;
      }
    },

    /**
     * 取消移除卡片
     * @author Nemo
     */
    cancelRemoveCard() {
      this.pendingRemoveCardId = null;
    },

    /**
     * 切换到新卡支付
     * @author Nemo
     */
    handleSwitchToCard() {
      this.$emit('switch-to-card');
    },

    /**
     * 获取品牌色
     * @author Nemo
     */
    getBrandColor() {
      const brandColor = getComputedStyle(document.documentElement).getPropertyValue('--brand-color').trim();
      this.brandColor = brandColor || '#182058';
    },

    /**
     * 计算 hover 颜色（加深8%）
     * @author Nemo
     */
    getHoverColor(color) {
      // 对于颜色值不存在的情况，返回默认加深色
      if (!color) return '#121643';

      // 处理十六进制颜色
      if (color.startsWith('#')) {
        try {
          // 解析十六进制颜色
          const hex = color.substring(1);
          let r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
          let g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
          let b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);

          // 加深颜色 (减少8%)
          r = Math.round(r * 0.92);
          g = Math.round(g * 0.92);
          b = Math.round(b * 0.92);

          // 转回十六进制
          return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        } catch (e) {
          return '#121643';
        }
      }

      // 处理rgb/rgba颜色
      if (color.startsWith('rgb')) {
        try {
          const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/);
          if (matches) {
            const r = Math.round(parseInt(matches[1]) * 0.92);
            const g = Math.round(parseInt(matches[2]) * 0.92);
            const b = Math.round(parseInt(matches[3]) * 0.92);
            const a = matches[4] ? matches[4] : '1';
            return `rgba(${r}, ${g}, ${b}, ${a})`;
          }
        } catch (e) {
          return '#121643';
        }
      }

      // 对于其他格式颜色，返回默认加深色
      return '#121643';
    }
  }
};
</script>

<style lang="scss" scoped>
.token-payment-container {
  padding: 0;

  .saved-cards-list {
    margin-bottom: 24px;

    .cards-list-content {
      position: relative;
    }

    .card-item {
      display: flex;
      flex-direction: column;
      padding: 12px 16px;
      border: 1px solid #D0D5DD;
      border-radius: 8px;
      margin-bottom: 4px;
      cursor: pointer;
      background-color: #fff;
      position: relative;

      &:hover:not(.expired) {
        border-color: var(--hover-brand-color);
      }

      &.active {
        border: 1px solid var(--brand-color);
        box-shadow: 0 0 0 1px var(--brand-color);
      }

      &.expired {
        background-color: #D0D5DD;

        .card-number {
          color: #888892;
        }
      }

      .card-item-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        gap: 16px;
      }

      .card-brand {
        width: 42px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;

        img {
          max-width: 100%;
          height: 28px;
          object-fit: contain;
        }
      }

      .card-number {
        font-family: "Open Sans SemiBold";
        font-size: 16px;
        color: #1F2937;
        flex: 0 0 auto;
      }

      .expired-tag {
        font-family: "Open Sans SemiBold";
        width: 56px;
        height: 20px;
        padding: 2px 6px;
        background-color: #C6C6CD;
        color: #41414C;
        font-size: 12px;
        line-height: 16px;
        border-radius: 4px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: auto;
        transition: all 0.3s ease;
      }

      .remove-btn {
        display: flex;
        align-items: center;
        cursor: pointer;
        white-space: nowrap;
        margin-left: auto;
        flex-shrink: 0;

        .delete-icon-wrapper {
          width: 12px;
          height: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 4px;
        }

        .delete-icon {
          width: 8.35px;
          height: 9.6px;
        }

        .remove-text {
          font-family: "Open Sans bold";
          font-size: 12px;
          line-height: 16px;
          letter-spacing: 0%;
          vertical-align: middle;
          color: #182058;
        }

        &:hover .remove-text {
          text-decoration: underline;
        }
      }

      .card-verify-section {
        width: 100%;
        margin-top: 16px;
      }
    }
  }

  .token-pay-btn-wrapper {
    margin-bottom: 20px;
  }

  .switch-to-card-wrapper {
    text-align: center;

    .use-new-card-btn {
      color: var(--brand-color);
      font-family: "Open Sans regular";
      font-weight: 500;
      cursor: pointer;
      font-size: 16px;
      line-height: 22px;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

/* 折叠动画样式 - 开发者：Nemo */
.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s;
  overflow: hidden;
}

.collapse-enter,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave {
  max-height: 1000px;
  opacity: 1;
}
</style>
